import { english, spanish, type LanguageProfile } from '../core/language';
import { themeExpansion, spanishThemeExpansion, spanishEveryday } from './expanded-vocabulary';

export interface WordPack {
  id: string;
  version: string;
  nameKey: string;
  language: LanguageProfile;
  words: string[];
  accent: string;
  source: string;
}

const pack = (id: string, words: string, accent: string): WordPack => ({
  id, version: '1', nameKey: `pack.${id}`, language: english,
  words: words.split(' '), accent, source: 'WordHuli original thematic selection',
});

export const packs: WordPack[] = [
  pack('forest', 'FERN MOSS PINE OAK CEDAR LEAF ROOT BARK GROVE TRAIL ACORN BIRCH MAPLE WILLOW DEER FOX OWL ROBIN CREEK SHADE BLOOM SEED', '#87c9a5'),
  pack('ocean', 'WAVE TIDE REEF CORAL SHELL PEARL SAND SHORE FOAM KELP WHALE SEAL CRAB SQUID SHARK RAY DOLPHIN LAGOON ISLAND CURRENT ANCHOR SAIL', '#89cce0'),
  pack('cosmos', 'STAR MOON SUN ORBIT COMET PLANET GALAXY NEBULA NOVA METEOR SOLAR LUNAR SPACE ROCKET SATURN VENUS MARS EARTH PLUTO AURORA ECLIPSE COSMOS', '#c0b2ef'),
  pack('garden', 'ROSE TULIP DAISY IRIS LILY ASTER PETAL STEM SEED SOIL WATER BLOOM SPROUT HERB MINT BASIL THYME SAGE APPLE PEACH PLUM BERRY', '#efb6c8'),
  pack('kitchen', 'BREAD RICE PASTA SOUP SALAD BEAN CORN APPLE PEAR MELON LEMON LIME HONEY SPICE SALT SUGAR FLOUR OVEN PLATE SPOON CUP FORK', '#ebc58e'),
];

export const packById = (id: string): WordPack => packs.find(item => item.id === id) ?? packs[0]!;
// Version 2 adds targets without changing old letters or target meanings.
export const compatiblePackVersion = (item: WordPack, savedVersion: string): boolean => item.version === savedVersion
  || savedVersion === '1' && item.version === '2' && packs.some(pack => pack.id === item.id && pack.version === '2' && pack.id !== 'general-es');

const spanishWords = [
  'HELECHO MUSGO PINO ROBLE CEDRO HOJA RAÍZ CORTEZA BOSQUE SENDERO BELLOTA ABEDUL ARCE SAUCE CIERVO ZORRO BÚHO ARROYO SOMBRA FLOR SEMILLA ÁRBOL',
  'OLA MAREA ARRECIFE CORAL CONCHA PERLA ARENA COSTA ESPUMA ALGA BALLENA FOCA CANGREJO CALAMAR TIBURÓN RAYA DELFÍN LAGUNA ISLA CORRIENTE ANCLA VELA',
  'ESTRELLA LUNA SOL ÓRBITA COMETA PLANETA GALAXIA NEBULOSA NOVA METEORO SOLAR LUNAR ESPACIO COHETE SATURNO VENUS MARTE TIERRA PLUTÓN AURORA ECLIPSE COSMOS',
  'ROSA TULIPÁN MARGARITA IRIS LIRIO PÉTALO TALLO SEMILLA TIERRA AGUA FLOR BROTE HIERBA MENTA ALBAHACA TOMILLO SALVIA MANZANA MELOCOTÓN CIRUELA JARDÍN RAÍZ',
  'PAN ARROZ PASTA SOPA ENSALADA FRIJOL MAÍZ MANZANA PERA MELÓN LIMÓN LIMA MIEL ESPECIA SAL AZÚCAR HARINA HORNO PLATO CUCHARA TAZA TENEDOR',
];
// Original thematic selections, expanded independently in each language.
packs.push(...packs.map((item, index) => ({ ...item, id: `${item.id}-es`, language: spanish, words: spanishWords[index]!.split(' ') })));
for (const item of packs) {
  const theme = item.id.replace(/-es$/, '');
  const additions = (item.language.locale === 'es' ? spanishThemeExpansion : themeExpansion)[theme] ?? '';
  item.words = [...new Set([...item.words, ...additions.split(' ').filter(Boolean)])];
  item.version = '2';
}
export const packsForLocale = (locale: string): WordPack[] => packs.filter(item => item.language.locale === locale);

// Curated fallback stays available when the offline dictionary cannot be read.
packs.unshift({ ...pack('general', packs.filter(item => item.language.locale === 'en').flatMap(item => item.words).join(' '), '#87c9a5'), source: 'Permitted WordNet vocabulary; curated fallback when unavailable' });
packs.push({ ...pack('general-es', '', '#87c9a5'), version: '2', nameKey: 'pack.general', language: spanish,
  words: [...new Set([...packs.filter(item => item.language.locale === 'es').flatMap(item => item.words), ...spanishEveryday])],
  source: 'WordHuli original Spanish thematic and everyday vocabulary selections' });
